import type { ComputedRef, InjectionKey, Ref } from "vue"
import { inject, provide } from "vue"
import type { BlockInstance, CartSettings } from "@dk/theme"

/** Shared state a cart-drawer block needs.
 *
 *  Same shape and rationale as `lib/pdp.ts`: CartDrawer owns the state (timers,
 *  upsell fetching, theme-setting fallbacks) and blocks read it through
 *  provide/inject, because a `<component :is>` loop has no static call site to
 *  declare props on.
 *
 *  Money stays server-authoritative — nothing here prices anything. The note and
 *  discount code are held client-side exactly as before (they are applied by the
 *  server at checkout), and the gift line is decided by the server.
 */
export interface CartDrawerContext {
  /** the Pinia cart store, used directly by the line-items block */
  cart: Record<string, unknown>
  empty: ComputedRef<boolean>
  /** Theme-level Cart settings — still the fallback when a block setting is left
   *  blank, so a store configured before Phase 4 keeps its badges/heading. Typed
   *  loosely on purpose: blocks read arbitrary keys and `CartSettings` has no
   *  index signature. */
  settings: ComputedRef<CartSettings>

  countdown: ComputedRef<{ show: boolean; label: string; mmss: string }>
  freeShip: ComputedRef<{ threshold: number; remaining: number; pct: number }>
  savingsCents: ComputedRef<number>
  /** the `product-upsell` strip's offers, priced off the live variant */
  upsellItems: ComputedRef<UpsellItem[]>
  /** Switches one offer on (add) or off (remove its line). */
  toggleUpsell: (variantId: number) => void | Promise<void>
  /** variant id of the offer in flight, so only its own switch is disabled
   *  (0 = idle) */
  upsellBusy: Ref<number>
  /** policies-checkbox ticks this; the checkout button reads it */
  policiesOk: Ref<boolean>
  policiesRequired: Ref<boolean>

  /** The seller's `main-cart` blocks, in order, with legacy upsell block types
   *  already merged (see @dk/theme's `mergeProductUpsellBlocks`). Resolved by the
   *  host rather than by CartBlocks so that a block can ask what ELSE was placed. */
  blocks: ComputedRef<BlockInstance[]>
}

export interface UpsellItem {
  variant_id: number
  title: string
  /** The displayed price (lib/displayPrice): a price test's arm, else list. */
  display_cents: number
  compare_at_price_cents?: number | null
  image_src?: string
  product_handle: string
  /** true when the cart already holds this variant; `line_id` is then the line
   *  the switch removes. An offer in the cart stays in the strip — hiding it is
   *  what made the old "Add" button a one-way door. */
  selected: boolean
  line_id?: number
}

const CART_KEY: InjectionKey<CartDrawerContext> = Symbol("dk-cart-drawer")

export function provideCartDrawer(ctx: CartDrawerContext): void {
  provide(CART_KEY, ctx)
}

export function useCartDrawer(): CartDrawerContext {
  const ctx = inject(CART_KEY)
  if (!ctx) throw new Error("useCartDrawer() must be called inside the cart drawer")
  return ctx
}
